/* eslint-disable no-console */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { HiPlus } from 'react-icons/hi';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import Swal from 'sweetalert2';
import { AxiosResponse } from 'axios';

import { subHours } from 'date-fns';
import api from '~/services/api';
import getValidationErros from '~/utils/getValidationsErrors';

import { Container, Filter, Modal, TableListModal } from './styles';
import InfiniteScroll from '~/components/InfiniteScroll';
import Select, { IOption } from '~/components/Select';
import FiltersLists from '~/components/FiltersLists';

import imgDefault from '~/assets/icons/image-default.svg';
import Loading from '~/components/Loading';
import Card from './Card';
import KrClose from '~/components/KoroIcons/KrClose';
import Input from '~/components/Input';
import Toast from '~/utils/toast';
import InputMask from '~/components/InputMask';
import formatDate from '~/utils/formatDate';
import Table, { IColumn } from '~/components/Table';
import ExportCsv from '~/components/ExportCsv';

export interface IEvent {
  id: number;
  archive_url: string;
  title: string;
  date: string;
  hour?: string;
  end_hour?: string;
  end_date?: string;
  type: string;
  type_image_url: string;
  status: string;
  unlisted: boolean;
  endRegistration: string;
  purchases: number;
  allTickets: number;
  slug: string;
  analytics?: string;
  facebook?: string;
  addKart?: boolean;
  startPayment?: boolean;
  checkout?: boolean;
  password?: string;
}

export interface ICheckIn {
  id: number;
  event_id: number;
  code: string;
  start_date: string;
  end_date: string;
  expected: number;
  read: number;
  status: string;
}

interface IEventCodeResponse {
  data: ICheckIn[];
  from: number;
  to: number;
  total: number;
  pages: number;
}

interface ICheckList {
  id: string;
  name: string;
  email: string;
  document: string;
  event: string;
  modality: string;
  category: string;
  read: string;
  date: string;
}

const CheckIn: React.FC = () => {
  const formRefCheckIn = useRef<FormHandles>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [modalCheckIn, setModalCheckIn] = useState(false);
  const [modalList, setModalList] = useState(false);
  const [eventsModal, setEventsModal] = useState<IOption[]>([]);
  const [eventName, setEventName] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState(eventName);
  const [password, setPassword] = useState('');
  const [editing, setEditing] = useState(false);
  const [checkInSelected, setCheckInSelected] = useState({} as ICheckIn);
  const [checkIns, setCheckIns] = useState<ICheckIn[]>([]);
  const [eventFilter, setEventFilter] = useState<string | undefined>(undefined);
  const [eventList, setEventList] = useState<ICheckList[]>([]);
  const [statusCheckin, setStatusCheckin] = useState<string | undefined>(
    undefined
  );
  const [dateStartSelected, setDateStartSelected] = useState<
    string | undefined
  >(undefined);
  const [dateEndSelected, setDateEndSelected] = useState<string | undefined>(
    undefined
  );
  const [eventSelected, setEventSelected] = useState<IEvent | undefined>(
    undefined
  );

  const [eventListFiltered, setEventListFiltered] = useState<ICheckList[]>([]);
  const [search, setSearch] = useState('');
  const [fillBefore, setFillBefore] = useState<number>();

  const columnsModal = useMemo<IColumn[]>(
    () => [
      {
        name: 'Id Inscrição',
        selector: 'id',
        sortable: true,
      },
      {
        name: 'Status',
        selector: 'status',
        sortable: true,
      },
      {
        name: 'Lido',
        selector: 'read',
        sortable: true,
      },
      {
        name: `${fillBefore === 2 ? 'Nome comprador' : 'Nome'}`,
        selector: 'name',
        sortable: true,
        cell: (row: ICheckList) => <span title={row.name}>{row.name}</span>,
      },
      {
        name: `${fillBefore === 2 ? 'CPF comprador' : 'CPF'}`,
        selector: 'document',
        sortable: true,
      },
      {
        name: `${fillBefore === 2 ? 'E-mail comprador' : 'E-mail'}`,
        selector: 'email',
        sortable: true,
        cell: (row: ICheckList) => <span title={row.email}>{row.email}</span>,
      },

      {
        name: 'Evento',
        selector: 'event',
        sortable: true,
      },
      {
        name: 'Modalidade',
        selector: 'modality',
        sortable: true,
      },
      {
        name: 'Categoria',
        selector: 'category',
        sortable: true,
      },

      {
        name: 'Data/ Hora da leitura',
        selector: 'date',
        sortable: true,
      },
    ],
    [fillBefore]
  );

  const status = useMemo<IOption[]>(
    () => [
      {
        id: 'Cadastrado',
        value: 'Cadastrado',
        selected: false,
      },
      {
        id: 'Em Andamento',
        value: 'Em Andamento',
        selected: false,
      },
      {
        id: 'Encerrado',
        value: 'Encerrado',
        selected: false,
      },
    ],
    []
  );

  const handleLoadCheckIns = useCallback(
    async (
      pageData,
      statusCheckIn?: string,
      event?: string,
      dateStart?: string,
      dateEnd?: string
    ) => {
      try {
        setLoadingMore(true);
        const response = await api.get<IEventCodeResponse>(
          'events-codes/organizers',
          {
            params: {
              page: pageData,
              event,
              status: statusCheckIn,
              startDate: dateStart ? new Date(dateStart) : undefined,
              endDate: dateEnd ? new Date(dateEnd) : undefined,
            },
          }
        );

        if (pageData === 1) {
          setCheckIns(response.data.data);
        } else {
          setCheckIns((oldState) => [...oldState, ...response.data.data]);
        }
        setTotalPages(response.data.pages);
      } catch (error) {
        console.log(error);
      } finally {
        setLoadingMore(false);
      }
    },
    []
  );

  const handleLoadEventsModal = useCallback(
    async (searchModal = '') => {
      const response = await api.get('events/organizer', {
        params: {
          search: searchModal.trim(),
          status_ids: [1, 2, 3, 4, 5],
        },
      });

      const data = response.data.map((eventData: any) => ({
        id: eventData.id,
        value: eventData.name,
        selected: false,
      })) as IOption[];

      if (data.length === 0) {
        data.push({
          id: 0,
          value: 'Nenhum evento encontrado',
          selected: false,
          notSelectable: true,
        });
      }

      let filtered = data.filter(
        (ev) => !checkIns.some((c) => c.event_id === ev.id)
      );

      if (editing && Object.keys(checkInSelected).length > 0) {
        const eventData = response.data.filter(
          (ev: any) => ev.id === checkInSelected.event_id
        );
        filtered = data.filter((ev) => ev.id === checkInSelected.event_id);
        filtered[0].selected = true;
        setEventName(filtered[0].value);
        setEventSelected(eventData);
      }

      setEventsModal(filtered);
    },
    [checkInSelected, checkIns, editing]
  );

  useEffect(() => {
    handleLoadEventsModal(debouncedSearch);
  }, [debouncedSearch, handleLoadEventsModal]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(eventName);
    }, 500);

    return () => clearTimeout(timer);
  }, [eventName]);

  const handleChangeTextEvent = useCallback((text) => {
    setEventName(text);
  }, []);

  const handleSelectEvent = useCallback(async (event) => {
    setEventName(event.value);
    const response = await api.get('events/organizer');

    const data = response.data.map((eventData: any) => ({
      id: eventData.id,
      archive_url: eventData.archive_path || imgDefault,
      title: eventData.name,
      date: eventData.date,
      end_date: eventData.end_date,
      hour: eventData.hour,
      end_hour: eventData.end_hour,
      type: eventData.type_name,
      type_image_url: eventData.type_archive_url,
      status: 'Cadastrado',
      password: eventData.password,
    })) as IEvent[];

    setEventSelected(data.find((eventData) => eventData.id === event.id));
  }, []);

  useEffect(() => {
    handleLoadCheckIns(1);
  }, [handleLoadCheckIns]);

  const handleShow = useCallback(() => {
    setModalCheckIn(true);
  }, []);

  const handleClickDelete = useCallback((checkIn) => {
    setCheckIns((oldState) =>
      oldState.filter((checkInData) => checkInData.id !== checkIn.id)
    );
  }, []);

  const handleLoad = useCallback(async () => {
    if (!loadingMore) {
      setLoadingMore(true);
      if (page < totalPages) {
        await handleLoadCheckIns(
          page + 1,
          eventFilter,
          statusCheckin !== 'Todos' ? statusCheckin : undefined,
          dateStartSelected,
          dateEndSelected
        );
        setPage(page + 1);
      } else {
        setLoadingMore(false);
      }
    }
  }, [
    loadingMore,
    page,
    totalPages,
    handleLoadCheckIns,
    eventFilter,
    statusCheckin,
    dateStartSelected,
    dateEndSelected,
  ]);

  const handleSubmitFilters = useCallback(
    (data) => {
      setEventFilter(data.event === 'Todos' ? undefined : data.event);
      setStatusCheckin(data.status);
      setDateStartSelected(data.dateStart);
      setDateEndSelected(data.dateEnd);
      handleLoadCheckIns(
        1,
        data.status !== 'todos' ? data.status : undefined,
        data.event === 'Todos' ? undefined : data.event,
        data.dateStart,
        data.dateEnd
      );
    },
    [handleLoadCheckIns]
  );

  const handleCloseCheckIn = useCallback(() => {
    setModalCheckIn(false);
    setEventSelected(undefined);
    setCheckInSelected({} as ICheckIn);
    setEventName('');
    setPassword('');
    setEditing(false);
  }, []);

  const handleLoadPurchases = useCallback(async (eventId?: number) => {
    const response = await api.get(`orders/events/${eventId}`);

    const purchases = response.data.reduce((previous: any, current: any) => {
      return previous + current.items.length;
    }, 0);
    return purchases;
  }, []);

  const handleSubmitCheckIn = useCallback(
    async (data) => {
      setLoadingMore(true);
      try {
        formRefCheckIn.current?.setErrors({});

        const schema = Yup.object().shape({
          event: Yup.string().required('O evento é obrigatório'),
          start_hour: Yup.string()
            .required('A hora de inicio é obrigatória')

            .test('valid-hour', 'Hora inválida', async (value) => {
              if (value) {
                const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
                return regex.test(value);
              }
              return true;
            }),
          end_hour: Yup.string()
            .required('A hora de fim é obrigatória')

            .test('valid-hour', 'Hora inválida', async (value) => {
              if (value) {
                const regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
                return regex.test(value);
              }
              return true;
            }),

          password: Yup.string()
            .required('O Código de acesso é obrigatório')
            .test('check-code', 'Erro - Codigo Repetido', async (value) => {
              if (value) {
                try {
                  if (checkInSelected.code === value) return true;
                  await api.get(`events-codes/organizers/${value}`);
                  return false;
                } catch (error) {
                  console.log(error);
                  return true;
                }
              }
              return true;
            }),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        let response: AxiosResponse;

        let [day, month, year] = data.start_date.split('/');
        const start_date = subHours(
          new Date(`${year}-${month}-${day} ${data.start_hour}`),
          3
        );

        [day, month, year] = data.end_date.split('/');
        const end_date = subHours(
          new Date(`${year}-${month}-${day} ${data.end_hour}`),
          3
        );

        const expected = await handleLoadPurchases(data.event);

        const formData = {
          event_id: data.event,
          code: data.password,
          start_date,
          end_date,
          expected,
          read: editing ? checkInSelected.read : 0,
        };

        const newCheckIns = checkIns.slice();

        if (editing) {
          response = await api.put(
            `events-codes/${checkInSelected.id}`,
            formData
          );
          const checkInIndex = newCheckIns.findIndex(
            (item) => item.id === checkInSelected.id
          );

          if (checkInIndex >= 0) {
            newCheckIns[checkInIndex].code = data.password;
            newCheckIns[checkInIndex].start_date = start_date.toISOString();
            newCheckIns[checkInIndex].end_date = end_date.toISOString();
            newCheckIns[checkInIndex].expected = expected;
          }

          Toast.fire({
            icon: 'success',
            iconColor: '#ff6900',
            title: 'Check-In atualizado com sucesso!',
          });
        } else {
          response = await api.post(`events-codes`, formData);

          newCheckIns.unshift({
            id: response.data.id,
            event_id: parseInt(data.event, 10),
            code: data.password,
            start_date: start_date.toISOString(),
            end_date: end_date.toISOString(),
            expected,
            read: 0,
            status: 'Cadastrado',
          });
          Toast.fire({
            icon: 'success',
            iconColor: '#ff6900',
            title: 'Check-In incluído com sucesso!',
          });
        }

        setCheckIns(newCheckIns);
        handleCloseCheckIn();
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErros(error);
          formRefCheckIn.current?.setErrors(errors);
        } else {
          Swal.fire({
            icon: 'error',
            iconColor: '#ff6900',
            title: 'Oops...',
            text: 'Ocorreu um erro ao cadastrar o check-in',
          });
        }
      } finally {
        setLoadingMore(false);
      }
    },
    [
      checkInSelected,
      checkIns,
      editing,
      handleCloseCheckIn,
      handleLoadPurchases,
    ]
  );

  const handleChangePassword = useCallback((event) => {
    setPassword(event.target.value);
  }, []);

  const handleEditCheckIn = useCallback((checkIn) => {
    setEditing(true);
    setCheckInSelected(checkIn);
    setModalCheckIn(true);
  }, []);

  const handleClickPublish = useCallback(
    (checkIn) => {
      const newCheckins = checkIns.slice();

      const checkInIndex = newCheckins.findIndex(
        (item) => item.id === checkIn.id
      );

      if (checkInIndex >= 0) {
        newCheckins[checkInIndex].status = 'Em Andamento';
      }

      setCheckIns(newCheckins);
    },
    [checkIns]
  );

  const handleChangeStartDate = useCallback((e) => {
    if (e.target.value.length === 19) {
      const date = new Date(e.target.value);
    }
  }, []);

  const handleChangeEndDate = useCallback((e) => {
    if (e.target.value.length === 19) {
      const date = new Date(e.target.value);
    }
  }, []);

  const handleCloseList = useCallback(() => {
    setModalList(false);
  }, []);

  const handleClickList = useCallback(async (checkIn) => {
    try {
      setLoadingMore(true);

      const response = await api.get(`tickets/events/${checkIn.event.id}`, {
        params: {
          allStatus: true,
          status: ['Disponível', 'Usado', 'Cortesia'],
        },
      });

      setFillBefore(response.data[0].batchCategory.batch.event.fill_before);

      const fillBeforeValue =
        response.data[0].batchCategory.batch.event.fill_before;

      setEventList(
        response.data.map((item: any) => ({
          id: `#${item.code}`,
          status: `${
            (item.status === 'Usado' && 'Preenchido') ||
            (item.status === 'Disponível' ? 'Não Preenchido' : item.status)
          }`,
          read: item.validated ? 'Sim' : 'Não',
          name: fillBeforeValue === 2 ? item.orderItem.order.name : item.name,
          document:
            fillBeforeValue === 2
              ? item.orderItem.order.user.additionalInformation.document
              : item.document,
          email:
            fillBeforeValue === 2 ? item.orderItem.order.email : item.email,
          event: checkIn.event.name,
          modality: item.modality?.name,
          category: item.batchCategory?.category.name,
          date: item.read
            ? formatDate(new Date(item.read), 'dd/MM/yyyy HH:mm:ss')
            : '-',
        }))
      );

      setEventListFiltered(
        response.data.map((item: any) => ({
          id: `#${item.code}`,
          status: `${
            (item.status === 'Usado' && 'Preenchido') ||
            (item.status === 'Disponível' ? 'Não Preenchido' : item.status)
          }`,
          read: item.validated ? 'Sim' : 'Não',
          name: fillBeforeValue === 2 ? item.orderItem.order.name : item.name,
          document:
            fillBeforeValue === 2
              ? item.orderItem.order.user.additionalInformation.document
              : item.document,
          email:
            fillBeforeValue === 2 ? item.orderItem.order.email : item.email,
          event: checkIn.event.name,
          modality: item.modality?.name,
          category: item.batchCategory?.category.name,
          date: item.read
            ? formatDate(new Date(item.read), 'dd/MM/yyyy HH:mm:ss')
            : '-',
        }))
      );

      setModalList(true);
    } catch (err) {
      console.log(err);
    } finally {
      setLoadingMore(false);
    }
  }, []);

  const handleSearch = () => {
    const value = search.toLowerCase().trim();
    const documentSearch = value.replace(/\D/g, '');

    const filtered = eventList.filter((item) => {
      const byId = item.id.toString().includes(value);
      const byName = (item.name || '').toLowerCase().includes(value);
      const byEmail = (item.email || '').toLowerCase().includes(value);

      const byDocument =
        documentSearch.length > 0 &&
        item.document?.replace(/\D/g, '').includes(documentSearch);

      return byId || byName || byEmail || byDocument;
    });

    setEventListFiltered(filtered);
  };

  return (
    <Container className="mt-n3 mb-n3">
      <InfiniteScroll
        id="infinite-scroll"
        onInfiniteLoad={handleLoad}
        className="infinite-scroll py-3"
      >
        <div className="container-fluid">
          <div className="row">
            <div className="zoom col-12 px-0">
              <div className="bg-white d-flex justify-content-between align-items-center p-4">
                <h1 className="mb-0">Check-In</h1>
                <div>
                  <button
                    type="button"
                    onClick={handleShow}
                    className="mb-0 me-4 h6 fw-normal text-white btn-new px-5 py-2"
                  >
                    <HiPlus size={16} color="#fff" className="me-2" />
                    Novo Check-In
                  </button>
                </div>
              </div>
            </div>
            <Filter className="col-12 px-0 mt-3">
              <FiltersLists
                onSubmitFilters={handleSubmitFilters}
                event
                statusCheckIn
                date
                statusOptions={status}
              />
            </Filter>

            <div className="zoom col-12 pe-0">
              {checkIns.map((checkIn) => (
                <Card
                  key={checkIn.id}
                  checkIn={checkIn}
                  onDeleteCheckIn={handleClickDelete}
                  onEditCheckIn={handleEditCheckIn}
                  onPublishCheckIn={handleClickPublish}
                  onListCheckIn={handleClickList}
                />
              ))}
            </div>
          </div>
        </div>
      </InfiniteScroll>
      <Modal
        className="modal-filter"
        size="lg"
        centered
        show={modalCheckIn}
        onHide={handleCloseCheckIn}
      >
        <Form
          ref={formRefCheckIn}
          initialData={checkInSelected}
          onSubmit={handleSubmitCheckIn}
        >
          <Modal.Header className="justify-content-between align-items-center border-0 px-3 px-sm-5 pb-0 pt-0">
            <Modal.Title className="mb-0 mt-4">
              <h2 className="title mb-0">Check-In</h2>
            </Modal.Title>
            <button
              type="button"
              onClick={handleCloseCheckIn}
              className="border-0 bg-transparent"
            >
              <KrClose size={20} color="#989898" />
            </button>
          </Modal.Header>
          <Modal.Body className="pt-0 px-3 px-sm-5">
            <div className="row">
              <div className="col-12">
                <hr />
              </div>

              <div className="col-12">
                <span className="d-block mb-1">Evento</span>

                <Select
                  name="event"
                  disabled={editing}
                  className={` bd-select`}
                  options={eventsModal}
                  onChangeText={handleChangeTextEvent}
                  value={eventName || eventSelected?.title}
                  onChange={handleSelectEvent}
                />
              </div>
              <div className="col-12">
                <hr />
              </div>
              <div className="col-12 d-flex">
                <label className="d-block me-3 w-50">
                  <span className="d-block mb-1">Início:</span>

                  <div className="d-flex border rounded">
                    <InputMask
                      kind="custom"
                      options={{ mask: '99/99/9999' }}
                      name="start_date"
                      className="pe-none border-0"
                      onChange={handleChangeStartDate}
                      placeholder="dd/mm/aaaa"
                      value={formatDate(
                        new Date(
                          checkInSelected.start_date ||
                            eventSelected?.date ||
                            new Date()
                        ),
                        'dd/MM/yyyy'
                      )}
                    />
                    <InputMask
                      kind="custom"
                      options={{ mask: '99:99' }}
                      name="start_hour"
                      className="border-0 mt-0"
                      onChange={handleChangeStartDate}
                      placeholder="hh:mm"
                      value={
                        editing
                          ? formatDate(
                              new Date(
                                checkInSelected.start_date || new Date()
                              ),
                              'HH:mm'
                            )
                          : undefined
                      }
                    />
                  </div>
                </label>
                <label className="d-block ms-3 w-50">
                  <span className="d-block mb-1">Fim:</span>
                  <div className="d-flex border rounded">
                    <InputMask
                      kind="custom"
                      options={{ mask: '99/99/9999' }}
                      name="end_date"
                      className="pe-none border-0"
                      onChange={handleChangeEndDate}
                      placeholder="dd/mm/aaaa"
                      value={formatDate(
                        new Date(
                          checkInSelected.end_date ||
                            eventSelected?.end_date ||
                            new Date()
                        ),
                        'dd/MM/yyyy'
                      )}
                    />
                    <InputMask
                      kind="custom"
                      options={{ mask: '99:99' }}
                      name="end_hour"
                      className="border-0 mt-0"
                      onChange={handleChangeEndDate}
                      placeholder="hh:mm"
                      value={
                        editing
                          ? formatDate(
                              new Date(checkInSelected.end_date || new Date()),
                              'HH:mm'
                            )
                          : undefined
                      }
                    />
                  </div>
                </label>
              </div>
              <div className="col-12">
                <hr />
              </div>
              <div className="col-12">
                <span className="h6 fw-normal mt-3 d-block">
                  Crie um código para o evento:
                </span>
                <Input
                  type="text"
                  name="password"
                  className="rounded"
                  onChange={handleChangePassword}
                  defaultValue={editing ? checkInSelected.code : undefined}
                />
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="border-0 pb-4 px-5">
            <button
              type="submit"
              disabled={!editing && (!eventSelected || !password)}
              className={`${
                eventSelected || password ? '' : 'opacity-50'
              } ms-auto btn-filter d-flex align-items-center justify-content-center border-0 h6 fw-normal py-2 px-5 mt-2 mb-0`}
            >
              Salvar
            </button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        className="modal-filter modal-width-100"
        size="lg"
        centered
        show={modalList}
        onHide={handleCloseList}
      >
        <Modal.Header className="border-0 px-3 px-sm-5 pb-0 pt-0">
          <Modal.Title className="mb-0 mt-4 w-100">
            <div className="d-flex align-items-center justify-content-between">
              <h2 className="title mb-0 me-3">
                Lista&nbsp;de&nbsp;presença - {eventListFiltered[0]?.event}
              </h2>
              <div className="d-flex align-items-center w-25 border rounded pe-2 me-4">
                <input
                  name="search"
                  type="text"
                  placeholder="Pesquisar na tabela..."
                  className="form-control border-0 me-3"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyPress={(e) =>
                    e.key === 'Enter' &&
                    (search.trim().length === 0 || search.trim().length >= 4) &&
                    handleSearch()
                  }
                />
                <button
                  type="button"
                  className="btn-search px-3 py-1"
                  disabled={
                    search.trim().length > 0 && search.trim().length < 4
                  }
                  onClick={handleSearch}
                >
                  Buscar
                </button>{' '}
              </div>
            </div>
          </Modal.Title>
          <button
            onClick={handleCloseList}
            type="button"
            className="border-0 bg-transparent"
          >
            <KrClose size={20} color="#989898" />
          </button>
        </Modal.Header>
        <Modal.Body className="pt-0 px-3">
          <div className="row">
            <div className="col-12">
              <hr />

              <TableListModal className="table-modal overflow-auto">
                <Table
                  className="p-0"
                  columns={columnsModal}
                  data={eventListFiltered}
                />
                {eventList.length > 0 && (
                  <div className="d-flex align-items-center justify-content-between">
                    <span className="ms-5">Total: {eventList.length}</span>
                    <ExportCsv
                      data={eventList}
                      columns={columnsModal}
                      fileName="Lista_de_presenca"
                      className="btn-new mt-lg-4"
                      onClick={async () => {
                        return eventList;
                      }}
                    />
                  </div>
                )}
              </TableListModal>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 pb-4 px-5"> </Modal.Footer>
      </Modal>
      <Loading active={loadingMore} text="Carregando..." />
    </Container>
  );
};

export default CheckIn;
